/**
 * Locale-aware number / currency formatting.
 *
 * Public API:
 *   formatNumber(amount, digits?)        – just the number, default tr-TR
 *   formatTRY(amount, digits?)           – number + " ₺" (legacy callers)
 *   formatMoney(amount, lang, opts?)     – number + currency symbol per `lang`
 *   parseTRYInput(value)                 – parse tr-TR string back to Number
 *
 * Examples:
 *   formatMoney(10000, 'tr') → "10.000,00 ₺"
 *   formatMoney(10000, 'en') → "$10,000.00"
 *   formatMoney(10000, 'es') → "10.000,00 €"
 *   formatMoney(10000, 'fr') → "10 000,00 €"
 */

// Per-language locale + currency symbol (matches backend LOCALE_CONFIG).
// Frontend can render the real Turkish Lira glyph "₺" since the system
// font supports it (only the PDF font lacks it).
const LOCALES = {
  tr: { locale: 'tr-TR', symbol: '₺', code: 'TRY', position: 'after' },
  en: { locale: 'en-US', symbol: '$', code: 'USD', position: 'before' },
  es: { locale: 'es-ES', symbol: '€', code: 'EUR', position: 'after' },
  fr: { locale: 'fr-FR', symbol: '€', code: 'EUR', position: 'after' },
  ar: { locale: 'ar-SA', symbol: 'ر.س', code: 'SAR', position: 'before' },
  de: { locale: 'de-DE', symbol: '€', code: 'EUR', position: 'after' },
};

const formatterFor = (locale, digits) =>
  new Intl.NumberFormat(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

/** Plain number with Turkish locale ("10.000,00"). */
export const formatNumber = (amount, digits = 2) => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return digits === 0 ? '0' : '0,00';
  return formatterFor('tr-TR', digits).format(n);
};

/** Plain number with the user's chosen locale (no currency symbol). */
export const formatNumberLocale = (amount, lang = 'tr', digits = 2) => {
  const cfg = LOCALES[lang] || LOCALES.tr;
  const n = Number(amount);
  if (!Number.isFinite(n)) return digits === 0 ? '0' : formatterFor(cfg.locale, digits).format(0);
  return formatterFor(cfg.locale, digits).format(n);
};

/** Legacy: number + " ₺" suffix (Turkish convention). */
export const formatTRY = (amount, digits = 2) => `${formatNumber(amount, digits)} ₺`;

/**
 * Locale-aware money formatting with the appropriate currency symbol position.
 *   formatMoney(10000, 'tr') → "10.000,00 ₺"
 *   formatMoney(10000, 'en') → "$10,000.00"
 */
export const formatMoney = (amount, lang = 'tr', { digits = 2 } = {}) => {
  const cfg = LOCALES[lang] || LOCALES.tr;
  const num = formatNumberLocale(amount, lang, digits);
  return cfg.position === 'before' ? `${cfg.symbol}${num}` : `${num} ${cfg.symbol}`;
};

/** Returns the currency symbol for the given language. */
export const currencySymbol = (lang = 'tr') => (LOCALES[lang] || LOCALES.tr).symbol;

/**
 * Parses a tr-TR-formatted string back to a Number for input handling.
 *   parseTRYInput("10.000,50") → 10000.5
 *   parseTRYInput("10000.50")  → 10000.5  (also accepts US format input)
 */
export const parseTRYInput = (value) => {
  if (value == null) return NaN;
  const cleaned = String(value)
    .replace(/\s|₺|\$|€|ر\.س/g, '')
    .replace(/\.(?=\d{3}(\D|$))/g, '') // strip thousand-dots
    .replace(',', '.');
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : NaN;
};
